// The "What do you want?" screen's state: the ask, the answers shown for it,
// and what the punter kept. docs/tips-ux.md is the state machine this follows.
//
// THE ANSWER IS A SNAPSHOT, NOT A COMPUTED. A batch of tickets is worked out when
// the punter asks and then held: the minute clock moving must not reshuffle the
// three tickets they are reading, and "Another one" appends to what is there.
// A new board (a refresh) is the one thing that re-asks by itself, because the
// prices under every ticket have just changed.
//
// PERSISTENCE follows the rest of the app: localStorage through `prefs`, never
// in /demo or on the sample board.

import { reactive, ref, computed, watch } from 'vue';
import { prefs } from '../../core/storage.js';
import { isDemo } from '../../core/demo.js';
import { scheduleDaily } from '../../core/notify.js';
import {
  universe, raceUniverse, pricesFetchedAt, racingCard, now, sampleMode, riskProfile, allResults,
} from '../../store.js';
import { answer, requestKey, interpret, normaliseRequest } from '../engine/ask.js';
import { chipsFor, requestFrom, nextState, hasOpinion } from '../ask/chips.js';
import { presetById, presetRequests } from '../ask/presets.js';
import { track, configureAnalytics } from '../ask/analytics.js';
import { askView } from './ask-view.js';

export const HISTORY_KEY = 'askHistory';
export const PINNED_KEY = 'askPinned';
export const SAVED_KEY = 'askSaved';
export const PREFS_KEY = 'askPrefs';
export const HISTORY_DAYS = 14;
export const HISTORY_PER_DAY = 60;
export const MAX_PINNED = 50;
export const MAX_SAVED = 12;
/** The list shows this many, then this many more as it is scrolled. */
export const PAGE = 5;
export const FIRST_COUNT = PAGE;

const writes = () => !isDemo.value && !sampleMode.value;
const read = (key, fallback) => {
  try { return prefs[key] ?? fallback; } catch { return fallback; }
};

configureAnalytics({
  isEnabled: () => writes(),
  platform: globalThis.Capacitor?.getPlatform?.() ?? 'web',
});

// --- the boards and the clock -------------------------------------------------------

/** Both boards the device holds, in the shape `answer` takes. */
export const askBoards = computed(() => [
  { universe: universe.value, kind: 'sports', fetchedAt: pricesFetchedAt.value },
  { universe: raceUniverse.value, kind: 'racing', fetchedAt: racingCard.value?.fetchedAt ?? null },
].filter((b) => b.universe && b.universe.size > 0));

/** When the answer is cut: the minute clock, never before the board was bought. */
export const askAsOf = computed(() => {
  const fetched = Date.parse(pricesFetchedAt.value ?? '') || 0;
  return new Date(Math.max(now.value, fetched)).toISOString();
});

export const hasAskBoard = computed(() => askBoards.value.length > 0);

// --- the ask -----------------------------------------------------------------------

// `include` is the legs the punter LOCKED — picked from a game, or kept off a
// ticket — as `${matchKey}:${side}` keys. Every answer is built around them.
// `book` is which bookmaker the multis are written at: 'any' (the default)
// means every book on the board, null means the punter's own book from Settings
// (Sportsbet by default), anything else is that book's key.
export const ANY_BOOK = 'any';

// `search` is what the punter typed ('' for nothing); `odds` a combined-price
// range `{ min, max }` or null for any price.
const FRESH = () => ({
  chips: {}, include: [], when: 'today', risk: null, legs: null, odds: null, search: '', strategy: 'best', book: ANY_BOOK,
});
export const askSelections = reactive(FRESH());

/** The book the answer is written at, or null for any book. */
export const askBook = computed(() => {
  if (askSelections.book === ANY_BOOK) return null;
  return askSelections.book ?? riskProfile.value.bookmaker ?? 'sportsbet';
});

/** Every book pricing something on the boards, by name, for the Bookmaker choice. */
export const askBooks = computed(() => {
  const names = new Map();
  for (const b of askBoards.value) {
    for (const match of b.universe.matches.values()) {
      for (const rows of Object.values(match.prices ?? {})) {
        for (const row of rows) {
          if (row.bookmaker === 'betfair_ex_au' || names.has(row.bookmaker)) continue;
          names.set(row.bookmaker, row.bookmakerName ?? row.bookmaker);
        }
      }
    }
  }
  return [...names.entries()].map(([key, name]) => ({ key, name })).sort((a, b) => a.name.localeCompare(b.name));
});

export function setBook(key) { askSelections.book = key; }

/** The list's order: best value, likeliest, or longest odds. */
export const SORTS = [
  { value: 'best', label: 'Best value' },
  { value: 'likely', label: 'Likeliest' },
  { value: 'longshots', label: 'Longest odds' },
];
export function setSort(value) { askSelections.strategy = value; }
export const askPresetId = ref(null);

export const askChips = computed(() => chipsFor(askBoards.value, { asOf: askAsOf.value }));

/** The requests the screen is asking right now: a preset's, or the chips'. */
export const askRequests = computed(() => {
  const book = askBook.value;
  const bookmakers = book ? [{ bookmaker: book, weight: 'only' }] : [];
  const chipRequest = normaliseRequest({ ...requestFrom(askSelections, askChips.value), bookmakers });
  const preset = presetById(askPresetId.value);
  if (preset?.requests) return presetRequests(preset, chipRequest).map(normaliseRequest);
  return [chipRequest];
});

export const askPreset = computed(() => presetById(askPresetId.value));

/** Tap a chip: Include → Only → Not → off (an event: Include → Not → off). */
export function tapChip(chip) {
  askSelections.chips[chip.key] = nextState(chip, askSelections.chips[chip.key] ?? null);
}

/** Long-press a chip: set it to exactly this. */
export function setChip(chip, state) {
  askSelections.chips[chip.key] = state;
}

/** Lock a leg into every multi, or unlock it. */
export function toggleLock(key) {
  const i = askSelections.include.indexOf(key);
  if (i >= 0) askSelections.include.splice(i, 1);
  else {
    // One side of a game at a time: locking Brisbane unlocks Fremantle.
    const game = key.slice(0, key.lastIndexOf(':'));
    askSelections.include = askSelections.include.filter((k) => k.slice(0, k.lastIndexOf(':')) !== game);
    askSelections.include.push(key);
  }
}

export const isLocked = (key) => askSelections.include.includes(key);

export function setWhen(when) { askSelections.when = when; }
// A risk band and a price range are two answers to one question (the engine
// takes the range when both are set), so choosing one lets go of the other.
export function setRisk(risk) {
  askSelections.risk = risk;
  if (risk) askSelections.odds = null;
  askPresetId.value = null;
}
export function setLegs(legs) { askSelections.legs = legs; askPresetId.value = null; }
/** A combined-price range, `{ min, max }` (either may be missing), or null for any. */
export function setOdds(odds) {
  const ok = odds && (Number.isFinite(odds.min) || Number.isFinite(odds.max));
  askSelections.odds = ok ? { ...(Number.isFinite(odds.min) ? { min: odds.min } : {}), ...(Number.isFinite(odds.max) ? { max: odds.max } : {}) } : null;
  if (ok) askSelections.risk = null;
  askPresetId.value = null;
}
/** What the punter typed into the list's search: a team, runner, venue or sport. */
export function setSearch(text) {
  askSelections.search = typeof text === 'string' ? text.slice(0, 40) : '';
}

export function choosePreset(id) {
  askPresetId.value = askPresetId.value === id ? null : id;
  if (askPresetId.value) track('preset', { preset: id });
}

export function clearAsk() {
  for (const k of Object.keys(askSelections.chips)) delete askSelections.chips[k];
  const { chips: _chips, ...rest } = FRESH();
  Object.assign(askSelections, rest);
  askPresetId.value = null;
  forgetPrefs();
}

export const askHasOpinion = computed(() => hasOpinion(askSelections) || !!askPresetId.value);

/**
 * Apply a loosening the Unsatisfied state offered: a leg count, "prefer X
 * instead of X only", "drop X", a window, "any risk".
 */
export function loosen(item) {
  if (item.dimension === 'legs') {
    const n = Number.parseInt(item.suggestion, 10);
    askSelections.legs = Number.isFinite(n) ? n : null;
  } else if (item.dimension === 'risk') { askSelections.risk = null; askSelections.odds = null; }
  else if (item.dimension === 'search') askSelections.search = '';
  else if (item.dimension === 'when') askSelections.when = 'today';
  else if (item.dimension === 'strategy') askSelections.strategy = 'best';
  else if (item.dimension === 'bookmaker') askSelections.book = ANY_BOOK;
  else if (item.dimension === 'sport' && item.value) {
    const key = `sport:${item.value}`;
    askSelections.chips[key] = /^prefer /.test(item.suggestion) ? 'prefer' : null;
  } else if (item.dimension === 'event' && item.value) askSelections.chips[`event:${item.value}`] = null;
  else if (item.dimension === 'include' && item.value) {
    askSelections.include = askSelections.include.filter((k) => k !== item.value);
  }
  askPresetId.value = askPreset.value?.requests ? askPresetId.value : null;
}

// --- remembered preferences -----------------------------------------------------------
//
// THE PUNTER'S STANDING CHOICES outlive a visit: "no greyhounds", three legs,
// their own book, the sort. What is about ONE day does not: the window, the
// search, a locked leg, a game chip (tomorrow's card has none of today's games).
// Saved in `askPrefs` a moment after they settle, read back at load, never in
// /demo or on the sample board, and forgotten by Start over.

export const PREFS_DEBOUNCE_MS = 300;

/** The part of the selections worth keeping, or null when it is all default. */
export function standingPrefs(sel = askSelections) {
  const chips = Object.fromEntries(Object.entries(sel.chips ?? {})
    .filter(([k, v]) => k.startsWith('sport:') && v).sort());
  const out = {
    ...(Object.keys(chips).length ? { chips } : {}),
    ...(sel.risk ? { risk: sel.risk } : {}),
    ...(sel.legs ? { legs: sel.legs } : {}),
    ...(sel.odds ? { odds: { ...sel.odds } } : {}),
    ...(sel.book !== ANY_BOOK ? { book: sel.book } : {}),
    ...(sel.strategy && sel.strategy !== 'best' ? { strategy: sel.strategy } : {}),
  };
  return Object.keys(out).length ? out : null;
}

const CHIP_WORDS = new Set(['include', 'only', 'prefer', 'avoid']);
const legsOk = (v) => (Number.isInteger(v) && v >= 1 && v <= 10)
  || (v && typeof v === 'object' && Number.isInteger(v.min) && Number.isInteger(v.max));
const oddsOk = (v) => v && typeof v === 'object' && (Number.isFinite(v.min) || Number.isFinite(v.max));

/** Put stored preferences back, taking only what still makes sense. */
export function restorePrefs(saved = read(PREFS_KEY, null)) {
  if (!saved || typeof saved !== 'object') return false;
  for (const [k, v] of Object.entries(saved.chips ?? {})) {
    if (k.startsWith('sport:') && CHIP_WORDS.has(v)) askSelections.chips[k] = v;
  }
  if (['low', 'medium', 'high', 'outrageous'].includes(saved.risk)) askSelections.risk = saved.risk;
  if (legsOk(saved.legs)) askSelections.legs = typeof saved.legs === 'object' ? { min: saved.legs.min, max: saved.legs.max } : saved.legs;
  if (oddsOk(saved.odds)) { askSelections.odds = { ...saved.odds }; askSelections.risk = null; }
  if (saved.book === null || typeof saved.book === 'string') askSelections.book = saved.book;
  if (SORTS.some((o) => o.value === saved.strategy)) askSelections.strategy = saved.strategy;
  return true;
}

let prefsTimer = null;
function forgetPrefs() {
  clearTimeout(prefsTimer);
  prefsTimer = null;
  if (writes()) { try { delete prefs[PREFS_KEY]; } catch { /* private mode */ } }
}
/** Write the standing choices now (the debounced watcher's body). */
export function persistPrefs() {
  clearTimeout(prefsTimer);
  prefsTimer = null;
  if (!writes()) return;
  const keep = standingPrefs();
  try {
    if (keep) prefs[PREFS_KEY] = keep;
    else delete prefs[PREFS_KEY];
  } catch { /* private mode */ }
}

if (!isDemo.value) restorePrefs();
watch(() => JSON.stringify(standingPrefs()), () => {
  clearTimeout(prefsTimer);
  prefsTimer = setTimeout(persistPrefs, PREFS_DEBOUNCE_MS);
});

// --- the answer --------------------------------------------------------------------

export { askView };

const shownKeys = () => askView.batches.flatMap((b) => [...b.results.map((t) => t.multi.askKey), ...(b.dismissed ?? [])]);

function freshnessFields(freshness) {
  const of = (kind) => freshness.find((f) => f.kind === kind)?.ageMinutes;
  return { staleSportsMin: of('sports'), staleRacingMin: of('racing') };
}

function runAll(requests, { exclude = [], count } = {}) {
  const preset = askPreset.value;
  const per = preset?.per ?? count ?? FIRST_COUNT;
  const out = { results: [], unsatisfied: null, relaxed: [], freshness: [], interpretation: '', matching: 0 };
  for (const r of requests) {
    const a = answer(askBoards.value, { ...r, excludeMultis: [...r.excludeMultis, ...exclude] }, {
      asOf: askAsOf.value, count: per, stake: Number(riskProfile.value.defaultStake) || 10,
    });
    out.results.push(...a.results);
    out.matching += a.relaxed.length ? 0 : (a.stats?.matching ?? 0);
    out.unsatisfied = out.unsatisfied ?? a.unsatisfied;
    out.relaxed.push(...a.relaxed);
    out.freshness = a.freshness;
    out.interpretation = out.interpretation || a.interpretation;
  }
  if (preset) out.interpretation = `${preset.label} · ${out.interpretation.split(' · ').pop()}`;
  return out;
}

/** Ask. Clears what was shown for the previous ask; keeps the history. */
export function ask() {
  const t0 = globalThis.performance?.now?.() ?? Date.now();
  const requests = askRequests.value;
  const preset = askPreset.value;
  askView.key = requests.map(requestKey).join('+');
  askView.batches = [];
  askView.legacy = null;
  if (preset?.legacy) {
    const run = allResults.value.find((r) => r.strategy.id === preset.legacy);
    askView.legacy = { tickets: (run?.tickets ?? []).filter((t) => t.legs?.length), notes: run?.notes ?? [] };
    askView.interpretation = `${preset.label} · the model's own picks on today's card`;
    askView.unsatisfied = null;
    askView.relaxed = [];
    return;
  }
  if (!hasAskBoard.value) return;
  const out = runAll(requests);
  askView.interpretation = out.interpretation || interpret(requests[0]);
  askView.batches = [{ at: askAsOf.value, results: out.results }];
  askView.matching = out.matching;
  askView.exhausted = out.results.length < (preset?.per ? requests.length : PAGE);
  askView.unsatisfied = out.unsatisfied;
  askView.relaxed = out.relaxed;
  askView.freshness = out.freshness;
  askView.firstResultMs = Math.round((globalThis.performance?.now?.() ?? Date.now()) - t0);
  remember(out.results);
  track('request', {
    request: requests[0],
    preset: preset?.id,
    results: out.results.length,
    satisfied: !out.unsatisfied,
    relaxed: out.relaxed.length > 0,
    ttfrMs: askView.firstResultMs,
    ...freshnessFields(out.freshness),
  });
}

/** "Another one" / "Show me 3 more": the same ask, nothing shown before. */
export function another(count = PAGE) {
  if (!hasAskBoard.value || askView.legacy || askView.exhausted) return 0;
  const out = runAll(askRequests.value, { exclude: shownKeys(), count });
  if (out.results.length) askView.batches.push({ at: askAsOf.value, results: out.results });
  if (out.results.length < count) askView.exhausted = true;
  remember(out.results);
  track('another', { request: askRequests.value[0], results: out.results.length, round: askView.batches.length, satisfied: out.results.length > 0 });
  return out.results.length;
}

/**
 * Up to three replacements for one leg: the ticket's other legs kept, its book
 * kept, the ask's own musts, onlys and avoids kept, and nothing already shown.
 */
export function swapOptions(ticket, legKey) {
  const base = askRequests.value[0];
  const keep = ticket.legs.filter((l) => l.key !== legKey).map((l) => l.key);
  const swapped = ticket.legs.find((l) => l.key === legKey);
  const req = {
    ...base,
    // A must event on the leg being swapped out is still a must: the others keep
    // theirs by being pinned.
    events: base.events.filter((e) => e.weight !== 'must' || e.event === swapped?.matchKey || !keep.some((k) => k.startsWith(`${e.event}:`))),
    legs: ticket.legs.length,
    risk: null,
    odds: null,
    // Like for like: a swap for an AFL leg leans towards another AFL leg,
    // unless the ask already says which sports it wants.
    sports: base.sports.length || !swapped?.sport ? base.sports : [{ sport: swapped.sport, weight: 'prefer' }],
    include: keep,
    exclude: [...base.exclude, legKey],
    bookmakers: [{ bookmaker: ticket.multi.bookmakerKey, weight: 'only' }],
    excludeMultis: [...base.excludeMultis, ...shownKeys()],
  };
  const a = answer(askBoards.value, req, { asOf: askAsOf.value, count: 3, stake: ticket.multi.stake });
  return { options: a.results, unsatisfied: a.results.length ? null : a.unsatisfied };
}

export function applySwap(ticket, replacement) {
  for (const b of askView.batches) {
    const i = b.results.findIndex((t) => t.multi.id === ticket.multi.id);
    if (i >= 0) b.results.splice(i, 1, replacement);
  }
  remember([replacement]);
  track('swap', { request: askRequests.value[0] });
}

/** "Hide": off this answer, and still counted as shown, so Another never brings it back. */
export function dismiss(ticket) {
  for (const b of askView.batches) {
    const i = b.results.findIndex((t) => t.multi.id === ticket.multi.id);
    if (i >= 0) {
      b.dismissed = [...(b.dismissed ?? []), b.results[i].multi.askKey];
      b.results.splice(i, 1);
    }
  }
}

// Re-ask when the prices under the tickets change, and when the ask changes.
// ONE RE-ASK PER CHANGE, NOT ONE PER WATCHER. At start-up the board, the racing
// card and the request all settle within a tick of each other, and each used to
// run the whole search; they now schedule one run between them.
let pending = null;
export function scheduleAsk() {
  if (pending) return;
  pending = setTimeout(() => {
    pending = null;
    if (hasAskBoard.value || askPreset.value?.legacy) ask();
  }, 0);
}
watch(() => [pricesFetchedAt.value, racingCard.value?.fetchedAt ?? null], scheduleAsk);
watch(() => JSON.stringify([askRequests.value, askPresetId.value]), scheduleAsk);

/** Seen: once per screen visit, with how old the odds were. */
export function viewed() {
  track('view', { ...freshnessFields(askView.freshness) });
}

// --- history, pins, saved asks ---------------------------------------------------------

const today = () => askAsOf.value.slice(0, 10);

/** A ticket as history keeps it: everything the screen prints, nothing else. */
const compact = (t) => ({
  multi: {
    id: t.multi.id, askKey: t.multi.askKey, forDate: t.multi.forDate, bookmaker: t.multi.bookmaker,
    bookmakerKey: t.multi.bookmakerKey, combinedOdds: t.multi.combinedOdds, combinedProbability: t.multi.combinedProbability,
    fairProbability: t.multi.fairProbability, ev: t.multi.ev, tier: t.multi.tier, stake: t.multi.stake,
    potentialReturn: t.multi.potentialReturn, oddsFetchedAt: t.multi.oddsFetchedAt, strategyId: t.multi.strategyId,
    label: t.multi.label, status: 'proposed',
  },
  legs: t.legs.map((l) => ({
    key: l.key, matchKey: l.matchKey, selection: l.selection, match: l.match, sport: l.sport, sportTitle: l.sportTitle,
    commenceTime: l.commenceTime, price: l.price, side: l.side, bookmakerKey: l.bookmakerKey,
    modelProbability: l.modelProbability, eventId: l.eventId, id: l.id, multiId: l.multiId,
  })),
});

export const askHistory = ref(read(HISTORY_KEY, {}));
export const askPinned = ref(read(PINNED_KEY, []));
export const askSaved = ref(read(SAVED_KEY, []));

function remember(results) {
  if (!results.length) return;
  const date = today();
  const day = askHistory.value[date] ?? [];
  // The same answer again — a re-ask on a board that did not move, a second
  // visit — is not something new the punter was shown.
  const keys = results.map((t) => t.multi.askKey).sort().join(',');
  if (day.some((h) => h.tickets.map((t) => t.multi.askKey).sort().join(',') === keys)) return;
  day.unshift({ at: new Date(now.value).toISOString(), interpretation: askView.interpretation, key: askView.key, tickets: results.map(compact) });
  const next = { ...askHistory.value, [date]: day.slice(0, HISTORY_PER_DAY) };
  const cutoff = new Date(Date.parse(`${date}T00:00:00Z`) - HISTORY_DAYS * 86_400_000).toISOString().slice(0, 10);
  for (const d of Object.keys(next)) if (d < cutoff) delete next[d];
  askHistory.value = next;
  if (writes()) prefs[HISTORY_KEY] = next;
}

export const historyToday = computed(() => askHistory.value[today()] ?? []);

export const isPinned = (ticket) => askPinned.value.some((p) => p.ticket.multi.askKey === ticket.multi.askKey);

export function togglePin(ticket) {
  const list = askPinned.value.filter((p) => p.ticket.multi.askKey !== ticket.multi.askKey);
  if (list.length === askPinned.value.length) {
    list.unshift({ at: new Date(now.value).toISOString(), interpretation: askView.interpretation, ticket: compact(ticket) });
    track('pin', { request: askRequests.value[0] });
  }
  askPinned.value = list.slice(0, MAX_PINNED);
  if (writes()) prefs[PINNED_KEY] = askPinned.value;
}

/** Save the current ask by name; the daily reminder names it from then on. */
export function saveAsk(name) {
  const clean = String(name ?? '').trim().slice(0, 40) || askView.interpretation.split(' · ')[0];
  const entry = {
    id: `${Date.now().toString(36)}`,
    name: clean,
    presetId: askPresetId.value,
    selections: JSON.parse(JSON.stringify(askSelections)),
  };
  askSaved.value = [entry, ...askSaved.value.filter((s) => s.name !== clean)].slice(0, MAX_SAVED);
  persistSaved();
  return entry;
}

export function removeSaved(id) {
  askSaved.value = askSaved.value.filter((s) => s.id !== id);
  persistSaved();
}

/** Put a saved ask back on the screen, chip for chip. */
export function loadSaved(entry) {
  clearAsk();
  Object.assign(askSelections, {
    ...entry.selections,
    chips: { ...(entry.selections?.chips ?? {}) },
    include: [...(entry.selections?.include ?? [])],
  });
  askPresetId.value = entry.presetId ?? null;
}

function persistSaved() {
  if (!writes()) return;
  prefs[SAVED_KEY] = askSaved.value;
  if (riskProfile.value.notifyDaily === true) scheduleDaily(riskProfile.value.notifyAt);
}
